import { useEffect, useState, useMemo } from 'react'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import type { DialogProps, DialogSize } from '@wartek-id/dialog-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { useToast } from '@wartek-id/toast'

import { PelaksanaanStepResetCard } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanStepResetCard'
import { PRAKTIK_KINERJA_STEPS } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'
import { assetUrl } from 'utils/url'
import {
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIPostPelaksanaanAppealRequest,
} from 'api/kinerja/useAPIKinerja'
import {
  PRAKTIK_KINERJA_STEP_ORDER,
  PRAKTIK_KINERJA_RESETABLE_STEP_ORDER,
} from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'

import type {
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

type PelaksanaanAppealRequestDialogProps = Omit<DialogProps, 'children'> & {
  currentStep: TStatePelaksanaan
  appealStep: TPelaksanaanAppealableStep
}

export function PelaksanaanAppealRequestDialog({
  isOpen,
  currentStep,
  appealStep,
  onClose,
  ...dialogProps
}: PelaksanaanAppealRequestDialogProps) {
  const [activeContent, setActiveContent] = useState<
    'CONFIRMATION' | 'SUCCESS'
  >('CONFIRMATION')
  const toast = useToast()
  const queryClient = useQueryClient()

  // ----------------------------------------------------
  // Handle confirmation checkbox
  // ----------------------------------------------------
  const [isConfirmed, setIsConfirmed] = useState(false)
  const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConfirmed(e.target.checked)
  }

  // ----------------------------------------------------
  // Reset dialog upon dismissal
  // ----------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      setActiveContent('CONFIRMATION')
      setIsConfirmed(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // ----------------------------------------------------
  // Resolve steps to reset
  // ----------------------------------------------------
  const stepsToReset: Array<TStatePelaksanaan> = useMemo(() => {
    const appealStepOrder = PRAKTIK_KINERJA_STEP_ORDER.indexOf(appealStep)
    const currentStepOrder = PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentStep)

    // Appeal step must be before the current step
    if (appealStepOrder < currentStepOrder) {
      // Only include steps between appeal step and current step (inclusive)
      let slicedSteps = PRAKTIK_KINERJA_STEP_ORDER.slice(
        appealStepOrder,
        currentStepOrder
      )

      // Filter out steps that are not resetable
      slicedSteps = slicedSteps.filter((step) =>
        PRAKTIK_KINERJA_RESETABLE_STEP_ORDER.includes(step)
      )

      return slicedSteps
    }
    return []
  }, [appealStep, currentStep])

  // ----------------------------------------------------
  // Resolve dialog size
  // ----------------------------------------------------
  const resolvedDialogSize: DialogSize =
    activeContent === 'SUCCESS' ? 'sm' : 'md'

  // ----------------------------------------------------
  // Handle appeal mutation
  // ----------------------------------------------------
  const appealMutation = useAPIPostPelaksanaanAppealRequest()

  const handleConfirm = () => {
    appealMutation.mutate(
      { appealState: appealStep, tipe: 'GPK' },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
        },
        onError: () => {
          toast({
            message: 'Gagal lanjut. Coba lagi.',
          })
        },
      }
    )
  }

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      autoFocus={false}
      size={resolvedDialogSize}
      onClose={onClose}
      {...dialogProps}
    >
      <DialogOverlay />
      <DialogContent
        className="p-6"
        aria-labelledby="appeal-request-dialog-title"
      >
        {activeContent === 'CONFIRMATION' && (
          <div className="space-y-4">
            <Text variant="display-sm" id="appeal-request-dialog-title" as="h3">
              Ajukan perubahan ke Tim Kinerja. Berikut hal yang terdampak:
            </Text>
            <div
              className={clsx(
                'grid gap-4 mb-6',
                stepsToReset.length < 2 ? 'grid-cols-1' : 'grid-cols-2'
              )}
            >
              {stepsToReset.map((step, i) => {
                const stepData = PRAKTIK_KINERJA_STEPS[step]

                return (
                  <PelaksanaanStepResetCard
                    title={stepData.title}
                    description={stepData.resetMessage.pegawai}
                    imagePath={stepData.imagePath}
                    className="max-w-md"
                    key={i}
                  />
                )
              })}
            </div>

            <Checkbox
              value="accept"
              labelPosition="center"
              onChange={handleConfirmationChange}
              checked={isConfirmed}
            >
              <Text as="span" variant="body-bold">
                Saya setuju untuk mengulang pengisian
              </Text>
            </Checkbox>

            <div className="flex space-x-3 border-t pt-4 justify-end">
              <Button
                disabled={appealMutation.isLoading}
                color="white"
                size="lg"
                onClick={() => onClose?.()}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                disabled={appealMutation.isLoading || !isConfirmed}
                isLoading={appealMutation.isLoading}
                size="lg"
                onClick={handleConfirm}
              >
                Ajukan perubahan
              </Button>
            </div>
          </div>
        )}

        {activeContent === 'SUCCESS' && (
          <>
            <img
              src={assetUrl('/illustrations/img-time-document.png')}
              width={84}
              height={84}
              alt=""
              className="mb-6"
            />
            <Text
              variant="display-sm"
              className="mb-4"
              id="appeal-request-dialog-title"
              as="h3"
            >
              Perubahan sedang diajukan ke atasan
            </Text>
            <Text
              variant="body-lg"
              color="subdued"
              className="mb-6"
              id="appeal-request-dialog-description"
            >
              Status pengajuannya dapat dilihat di halaman Notifikasi atau cek
              lagi halaman ini nanti.
            </Text>
            <Button
              size="lg"
              onClick={() => {
                onClose?.()
                queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
              }}
            >
              Saya mengerti
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
